import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { ensureBlockIds } from '../utils/layoutHelpers';

const normalizeDashboardBlocks = dashboard => dashboard && Array.isArray(dashboard.blocks)
  ? { ...dashboard, blocks: ensureBlockIds(dashboard.blocks) }
  : dashboard;

// Actions asynchrones
export const fetchDashboards = createAsyncThunk(
  'pulse/fetchDashboards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: 'pulse/dashboards',
        method: 'GET',
      });
      return response.dashboards || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDashboard = createAsyncThunk(
  'pulse/fetchDashboard',
  async (dashboardId, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/dashboards/${dashboardId}`,
        method: 'GET',
      });
      return response.dashboard;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createDashboard = createAsyncThunk(
  'pulse/createDashboard',
  async (dashboardData, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: 'pulse/dashboards',
        method: 'POST',
        body: dashboardData,
      });
      return response.dashboard;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTemplates = createAsyncThunk(
  'pulse/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: 'pulse/templates',
        method: 'GET',
      });
      return response.templates || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createFromTemplate = createAsyncThunk(
  'pulse/createFromTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: 'pulse/dashboards/from-template',
        method: 'POST',
        body: templateData,
      });
      return response.dashboard;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateDashboard = createAsyncThunk(
  'pulse/updateDashboard',
  async ({ dashboardId, data }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/dashboards/${dashboardId}`,
        method: 'PATCH',
        body: data,
      });
      return response.dashboard;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteDashboard = createAsyncThunk(
  'pulse/deleteDashboard',
  async (dashboardId, { rejectWithValue }) => {
    try {
      await nodeAxiosFirebase({
        url: `pulse/dashboards/${dashboardId}`,
        method: 'DELETE',
      });
      return dashboardId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const executeDashboard = createAsyncThunk(
  'pulse/executeDashboard',
  async ({ dashboardId, filters }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const dateRange = state.pulse.dateRange;
      const dateRangePayload =
        dateRange?.[0] && dateRange?.[1]
          ? {
              start: moment(dateRange[0]).startOf('day').toISOString(),
              end: moment(dateRange[1]).endOf('day').toISOString(),
            }
          : null;

      const response = await nodeAxiosFirebase({
        url: `pulse/dashboards/${dashboardId}/run`,
        method: 'POST',
        body: {
          globalFilters: filters || [],
          dateRange: dateRangePayload,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Serialize writes per dashboard: quick edits must reach the server in order.
// Only send blocks; sending a template's structureMapping would recreate it.
const blockSaveQueues = new Map();
export const saveDashboardBlocks = createAsyncThunk(
  'pulse/saveDashboardBlocks',
  async ({ dashboardId, blocks: inputBlocks }, { rejectWithValue }) => {
    const blocks = ensureBlockIds(inputBlocks);
    const previous = blockSaveQueues.get(dashboardId) || Promise.resolve();
    const request = previous.catch(() => {}).then(() => nodeAxiosFirebase({
      url: `pulse/dashboards/${dashboardId}`,
      method: 'PATCH',
      body: { blocks },
    }));
    blockSaveQueues.set(dashboardId, request);
    try {
      const response = await request;
      return { dashboardId, dashboard: response.dashboard, blocks };
    } catch (error) {
      return rejectWithValue(error.message || 'Impossible d’enregistrer les blocs.');
    } finally {
      if (blockSaveQueues.get(dashboardId) === request) blockSaveQueues.delete(dashboardId);
    }
  }
);

// Which statuses mean open / lost / won for the dashboard (CRM pipeline template)
export const saveStatusMapping = createAsyncThunk(
  'pulse/saveStatusMapping',
  async ({ dashboardId, statusMapping }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/dashboards/${dashboardId}`,
        method: 'PATCH',
        body: { statusMapping },
      });
      return { dashboardId, statusMapping: response.dashboard?.statusMapping || statusMapping };
    } catch (error) {
      return rejectWithValue(error.message || 'Impossible d’enregistrer les statuts.');
    }
  }
);

const resolveBlockIndex = (blocks, blockId, blockIndex) => {
  if (Number.isInteger(blockIndex)) return blockIndex;
  const matchingId = ensureBlockIds(blocks).findIndex(block => block.id === String(blockId));
  if (matchingId >= 0) return matchingId;
  return /^block_\d+$/.test(blockId || '') ? Number(blockId.slice(6)) : -1;
};

export const updateBlockAndSave = createAsyncThunk(
  'pulse/updateBlockAndSave',
  async ({ blockId, blockIndex, updates }, { getState, dispatch, rejectWithValue }) => {
    try {
      const dashboard = getState().pulse.currentDashboard;
      if (!dashboard?.id) throw new Error('Aucun tableau de bord ouvert.');
      const index = resolveBlockIndex(dashboard.blocks || [], blockId, blockIndex);
      if (!dashboard.blocks?.[index]) throw new Error('Ce bloc n’existe plus.');
      const blocks = dashboard.blocks.map((block, i) => i === index ? { ...block, ...updates } : block);
      return await dispatch(saveDashboardBlocks({ dashboardId: dashboard.id, blocks })).unwrap();
    } catch (error) {
      return rejectWithValue(typeof error === 'string' ? error : error.message);
    }
  }
);

export const deleteBlockAndSave = createAsyncThunk(
  'pulse/deleteBlockAndSave',
  async (target, { getState, dispatch, rejectWithValue }) => {
    try {
      const dashboard = getState().pulse.currentDashboard;
      if (!dashboard?.id) throw new Error('Aucun tableau de bord ouvert.');
      const index = resolveBlockIndex(dashboard.blocks || [], typeof target === 'string' ? target : target.blockId, target?.blockIndex);
      if (!dashboard.blocks?.[index]) throw new Error('Ce bloc n’existe plus.');
      const blocks = dashboard.blocks.filter((_, i) => i !== index);
      return await dispatch(saveDashboardBlocks({ dashboardId: dashboard.id, blocks })).unwrap();
    } catch (error) {
      return rejectWithValue(typeof error === 'string' ? error : error.message);
    }
  }
);

export const fetchReports = createAsyncThunk(
  'pulse/fetchReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: 'pulse/reports',
        method: 'GET',
      });
      return response.reports || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchReport = createAsyncThunk(
  'pulse/fetchReport',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/reports/${reportId}`,
        method: 'GET',
      });
      return response.report;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createReport = createAsyncThunk(
  'pulse/createReport',
  async (reportData, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: 'pulse/reports',
        method: 'POST',
        body: reportData,
      });
      return response.report;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateReport = createAsyncThunk(
  'pulse/updateReport',
  async ({ reportId, data }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/reports/${reportId}`,
        method: 'PATCH',
        body: data,
      });
      return response.report;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteReport = createAsyncThunk(
  'pulse/deleteReport',
  async (reportId, { rejectWithValue }) => {
    try {
      await nodeAxiosFirebase({
        url: `pulse/reports/${reportId}`,
        method: 'DELETE',
      });
      return reportId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const executeReport = createAsyncThunk(
  'pulse/executeReport',
  async ({ reportId, parameters, limit, offset }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const dateRange = state.pulse.dateRange;
      const dateRangePayload =
        dateRange?.[0] && dateRange?.[1]
          ? {
              start: moment(dateRange[0]).startOf('day').toISOString(),
              end: moment(dateRange[1]).endOf('day').toISOString(),
            }
          : null;

      const response = await nodeAxiosFirebase({
        url: `pulse/reports/${reportId}/run`,
        method: 'POST',
        body: { parameters: parameters || {}, limit, offset, dateRange: dateRangePayload },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const previewReport = createAsyncThunk(
  'pulse/previewReport',
  async ({ reportId, parameters }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/reports/${reportId}/preview`,
        method: 'GET',
        params: { parameters: JSON.stringify(parameters || {}) },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const exportReport = createAsyncThunk(
  'pulse/exportReport',
  async ({ reportId, parameters, format }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `pulse/reports/${reportId}/export`,
        method: 'POST',
        body: { parameters: parameters || {}, format: format || 'csv' },
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// État initial
const initialState = {
  dashboards: [],
  currentDashboard: null,
  executionResults: null,
  reports: [],
  currentReport: null,
  reportResults: null,
  templates: [],
  loading: false,
  dashboardLoading: false,
  dashboardExecuting: false,
  dashboardRequestId: null,
  executionRequestId: null,
  error: null,
  filters: [],
  isEditMode: false,
  dateRange: [null, null],
  blockSaveStatus: 'idle',
  blockSaveError: null,
  blockSaveRequestId: null,
};

// Slice
const pulseSlice = createSlice({
  name: 'pulse',
  initialState,
  reducers: {
    setCurrentDashboard: (state, action) => {
      if (state.currentDashboard?.id !== action.payload?.id) {
        state.executionResults = null;
        state.dashboardExecuting = false;
        state.executionRequestId = null;
        state.blockSaveStatus = 'idle';
        state.blockSaveError = null;
        state.blockSaveRequestId = null;
      }
      state.currentDashboard = normalizeDashboardBlocks(action.payload);
    },
    setExecutionResults: (state, action) => {
      state.executionResults = action.payload;
    },
    updateBlockLayout: (state, action) => {
      const { blockId, layout } = action.payload;
      if (state.currentDashboard?.blocks) {
        const blockIndex = state.currentDashboard.blocks.findIndex(
          (block) => block.id === blockId
        );
        if (blockIndex !== -1) {
          state.currentDashboard.blocks[blockIndex].layout = layout;
        }
      }
    },
    addBlock: (state, action) => {
      if (state.currentDashboard?.blocks) {
        state.currentDashboard.blocks = ensureBlockIds([...state.currentDashboard.blocks, action.payload]);
      }
    },
    removeBlock: (state, action) => {
      const blockId = action.payload;
      if (state.currentDashboard?.blocks) {
        state.currentDashboard.blocks = state.currentDashboard.blocks.filter(
          (block) => block.id !== blockId
        );
      }
    },
    updateBlock: (state, action) => {
      const { blockId, updates } = action.payload;
      if (state.currentDashboard?.blocks) {
        const blockIndex = state.currentDashboard.blocks.findIndex(
          (block) => block.id === blockId
        );
        if (blockIndex !== -1) {
          state.currentDashboard.blocks[blockIndex] = {
            ...state.currentDashboard.blocks[blockIndex],
            ...updates,
          };
          // Mark dashboard as modified to trigger save - only if currentDashboard exists
          if (state.currentDashboard) {
            state.currentDashboard.modified = true;
          }
        }
      }
    },
    updateDashboardLayout: (state, action) => {
      const newLayout = action.payload;
      if (state.currentDashboard?.blocks) {
        state.currentDashboard.blocks = ensureBlockIds(state.currentDashboard.blocks).map(
          (block) => {
            // Match layout items with blocks using the same ID generation logic as generateLayout
            const blockId = block.id;
            const layoutItem = newLayout.find((item) => item.i === blockId);
            if (layoutItem) {
              return {
                ...block,
                layout: {
                  x: layoutItem.x,
                  y: layoutItem.y,
                  w: layoutItem.w,
                  h: layoutItem.h,
                },
              };
            }
            return block;
          }
        );
      }
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPulseDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
    addFilter: (state, action) => {
      state.filters.push(action.payload);
    },
    removeFilter: (state, action) => {
      const filterIndex = action.payload;
      state.filters.splice(filterIndex, 1);
    },
    clearError: (state) => {
      state.error = null;
    },
    setEditMode: (state, action) => {
      state.isEditMode = action.payload;
    },
    setCurrentReport: (state, action) => {
      state.currentReport = action.payload;
    },
    setReportResults: (state, action) => {
      state.reportResults = action.payload;
    },
    clearReportError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboards
      .addCase(fetchDashboards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboards = action.payload;
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Dashboard
      .addCase(fetchDashboard.pending, (state, action) => {
        state.dashboardLoading = true;
        state.dashboardRequestId = action.meta.requestId;
        if (state.currentDashboard?.id !== action.meta.arg) {
          state.currentDashboard = null;
          state.executionResults = null;
          state.dashboardExecuting = false;
          state.executionRequestId = null;
        }
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        if (state.dashboardRequestId !== action.meta.requestId) return;
        state.dashboardLoading = false;
        state.loading = false;
        state.currentDashboard = normalizeDashboardBlocks(action.payload);
        state.blockSaveStatus = 'idle';
        state.blockSaveError = null;
        state.blockSaveRequestId = null;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        if (state.dashboardRequestId !== action.meta.requestId) return;
        state.dashboardLoading = false;
        state.loading = false;
        state.error = action.payload;
      })
      // Create Dashboard
      .addCase(createDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboards.push(action.payload);
        state.currentDashboard = normalizeDashboardBlocks(action.payload);
      })
      .addCase(createDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Templates
      .addCase(fetchTemplates.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Create From Template
      .addCase(createFromTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFromTemplate.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.type === 'report') {
          state.reports.push(action.payload);
          state.currentReport = action.payload;
        } else {
          state.dashboards.push(action.payload);
          state.currentDashboard = normalizeDashboardBlocks(action.payload);
        }
      })
      .addCase(createFromTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Dashboard
      .addCase(updateDashboard.pending, (state) => {
        state.error = null;
      })
      .addCase(updateDashboard.fulfilled, (state, action) => {
        state.loading = false;
        // Keep the local currentDashboard as source of truth
        // The backend may return data in a different format (backend format vs frontend format)
        // so we only update the dashboards list if a valid dashboard is returned
        const updatedDashboard = action.payload;
        if (updatedDashboard?.id) {
          const index = state.dashboards.findIndex(
            (d) => d.id === updatedDashboard.id
          );
          if (index !== -1) {
            state.dashboards[index] = updatedDashboard;
          }
        }
      })
      .addCase(updateDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Dashboard
      .addCase(deleteDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.dashboards = state.dashboards.filter((d) => d.id !== deletedId);
        if (state.currentDashboard?.id === deletedId) {
          state.currentDashboard = null;
        }
      })
      .addCase(deleteDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Execute Dashboard
      .addCase(executeDashboard.pending, (state, action) => {
        if (state.currentDashboard?.id !== action.meta.arg.dashboardId) return;
        state.dashboardExecuting = true;
        state.executionRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(executeDashboard.fulfilled, (state, action) => {
        if (state.executionRequestId !== action.meta.requestId || state.currentDashboard?.id !== action.meta.arg.dashboardId) return;
        state.dashboardExecuting = false;
        state.loading = false;
        state.executionResults = action.payload;
      })
      .addCase(executeDashboard.rejected, (state, action) => {
        if (state.executionRequestId !== action.meta.requestId || state.currentDashboard?.id !== action.meta.arg.dashboardId) return;
        state.dashboardExecuting = false;
        state.loading = false;
        state.error = action.payload;
      })
      // Optimistic editing stays visible during writes and failures. A stale
      // response must never replace a more recent draft (or another dashboard).
      .addCase(saveDashboardBlocks.pending, (state, action) => {
        if (state.currentDashboard?.id !== action.meta.arg.dashboardId) return;
        if (action.meta.arg.optimistic !== false) state.currentDashboard.blocks = ensureBlockIds(action.meta.arg.blocks);
        state.blockSaveStatus = 'saving';
        state.blockSaveError = null;
        state.blockSaveRequestId = action.meta.requestId;
      })
      .addCase(saveDashboardBlocks.fulfilled, (state, action) => {
        if (state.currentDashboard?.id !== action.payload.dashboardId || state.blockSaveRequestId !== action.meta.requestId) return;
        state.blockSaveStatus = 'saved';
        state.blockSaveError = null;
        if (action.meta.arg.optimistic === false) state.currentDashboard.blocks = action.payload.blocks;
        const index = state.dashboards.findIndex(item => item.id === action.payload.dashboardId);
        if (index >= 0) state.dashboards[index] = { ...state.dashboards[index], blocks: action.payload.blocks };
      })
      .addCase(saveDashboardBlocks.rejected, (state, action) => {
        if (state.currentDashboard?.id !== action.meta.arg.dashboardId || state.blockSaveRequestId !== action.meta.requestId) return;
        state.blockSaveStatus = 'error';
        state.blockSaveError = action.payload || action.error.message;
      })
      .addCase(saveStatusMapping.fulfilled, (state, action) => {
        if (state.currentDashboard?.id === action.payload.dashboardId) {
          state.currentDashboard.statusMapping = action.payload.statusMapping;
        }
      })
      // Fetch Reports
      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Report
      .addCase(fetchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(fetchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Report
      .addCase(createReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reports.push(action.payload);
        state.currentReport = action.payload;
      })
      .addCase(createReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Report
      .addCase(updateReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReport.fulfilled, (state, action) => {
        state.loading = false;
        const updatedReport = action.payload;
        const index = state.reports.findIndex((r) => r.id === updatedReport.id);
        if (index !== -1) {
          state.reports[index] = updatedReport;
        }
        if (state.currentReport?.id === updatedReport.id) {
          state.currentReport = updatedReport;
        }
      })
      .addCase(updateReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Report
      .addCase(deleteReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReport.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.reports = state.reports.filter((r) => r.id !== deletedId);
        if (state.currentReport?.id === deletedId) {
          state.currentReport = null;
        }
      })
      .addCase(deleteReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Execute Report
      .addCase(executeReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportResults = action.payload;
      })
      .addCase(executeReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Preview Report
      .addCase(previewReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(previewReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportResults = action.payload;
      })
      .addCase(previewReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Export Report
      .addCase(exportReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportReport.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrentDashboard,
  setExecutionResults,
  updateBlockLayout,
  addBlock,
  removeBlock,
  updateBlock,
  updateDashboardLayout,
  setFilters,
  addFilter,
  removeFilter,
  clearError,
  setEditMode,
  setPulseDateRange,
  setCurrentReport,
  setReportResults,
  clearReportError,
} = pulseSlice.actions;

export default pulseSlice.reducer;
